import { useMemo, useState } from 'react';

import { Avatar, discordAvatarUrl } from '../../../components/Avatar';
import { SearchSelect } from '../../../components/SearchSelect';
import { useListUsersQuery } from '../../users/users.api';

export type AssigneeValue = { assigneeUserId: number; assigneeName: string } | null;

type Option = { id: number; label: string; sub: string | null; src: string | null };

type Props = {
    value: AssigneeValue;
    onChange: (value: AssigneeValue) => void;
    disabled?: boolean;
    // Restrict results to team members (role.level >= 2). Off for the reference
    // picker, which can point at any user (e.g. a customer on an order).
    teamOnly?: boolean;
    placeholder?: string;
};

// Server-search picker over the existing users API. Keeps the tickets backend
// decoupled — only `{ assigneeUserId, assigneeName }` (a snapshot) is sent.
export function AssigneePicker({
    value,
    onChange,
    disabled,
    teamOnly = true,
    placeholder = 'Cauta un coleg dupa nume...',
}: Props) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    const { data, isFetching } = useListUsersQuery(
        {
            search: query || undefined,
            active: true,
            limit: 25,
            // Ask the server for staff directly so an empty query prepopulates the
            // list with colleagues instead of the newest customer signups.
            minRoleLevel: teamOnly ? 2 : undefined,
        },
        { skip: !open },
    );

    const options: Option[] = useMemo(
        () =>
            (data?.data ?? [])
                .filter((u) => (teamOnly ? (u.role?.level ?? 0) >= 2 : true))
                .map((u) => ({
                    id: u.id,
                    label: u.name || u.email,
                    sub: u.name ? u.email : null,
                    src: discordAvatarUrl(u.discordId, u.discordAvatar),
                })),
        [data, teamOnly],
    );

    const selected: Option | null = value
        ? {
              id: value.assigneeUserId,
              // Fall back to the id when the snapshot name is missing, so the
              // control never renders a blank selection.
              label: value.assigneeName || `#${value.assigneeUserId}`,
              sub: null,
              src: null,
          }
        : null;

    return (
        <SearchSelect<Option>
            value={selected}
            onChange={(o) => onChange(o ? { assigneeUserId: o.id, assigneeName: o.label } : null)}
            items={options}
            loading={isFetching}
            onQueryChange={setQuery}
            onOpenChange={setOpen}
            getKey={(o) => o.id}
            getLabel={(o) => o.label}
            placeholder={placeholder}
            emptyText="Niciun coleg gasit"
            promptText="Scrie un nume pentru a cauta"
            disabled={disabled}
            ariaLabel="Cauta utilizator"
            renderItem={(o) => (
                <div className="flex min-w-0 items-center gap-2.5">
                    <Avatar src={o.src} name={o.label} seed={o.id} className="h-8 w-8 text-xs" />
                    <div className="min-w-0">
                        <span className="block truncate">{o.label}</span>
                        {o.sub && (
                            <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                                {o.sub}
                            </span>
                        )}
                    </div>
                </div>
            )}
        />
    );
}
